import React from "react";
import { Share2 } from "lucide-react";
import { useOrg } from "../../../contexts/OrganizationContext";
import { searchService } from "../../../services/searchService";
import { containsAnEmailAddress, isValidPhone } from "../../../utils/geo";

function ShareResult({ result, compact = false }) {
  const { organization } = useOrg();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("email");
  const [email, setEmail] = React.useState("");
  const [client, setClient] = React.useState("");
  const [comments, setComments] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [replyToMe, setReplyToMe] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [shareError, setShareError] = React.useState("");
  const [confirmation, setConfirmation] = React.useState(false);
  const [linkCopied, setLinkCopied] = React.useState(false);

  const enableSMS = organization.flags?.enableSMS;
  const isSignedIn = !!window.UW211?.CURRENT_USER;
  const programSiteId = result.program_site_id;
  const modalId = `shareModal${programSiteId}`;

  function handleOpen() {
    setModalOpen(true);
    setShareError("");
  }

  function handleClose() {
    setModalOpen(false);
  }

  function handlePrint() {
    setModalOpen(false);
    window.print();
  }

  function handleCopyLink() {
    const url = `${window.location.origin}/search/${programSiteId}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    });
  }

  async function handleSendEmail(e) {
    e.preventDefault();
    if (!email.trim() || !containsAnEmailAddress(email)) {
      setShareError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    setShareError(false);
    try {
      let agencyData = {};
      try {
        agencyData = await searchService.getAgency(result.agency_number, { siteNumber: result.site_number, programSiteId: result.program_site_id });
      } catch { /* optional */ }
      await searchService.shareResult({
        email,
        client,
        comments,
        replyToMe: isSignedIn ? replyToMe : false,
        programSiteId,
        result,
        agency: agencyData,
      });
      setModalOpen(false);
      setEmail("");
      setClient("");
      setComments("");
      setConfirmation(true);
      setTimeout(() => setConfirmation(false), 6000);
    } catch {
      setShareError("Unable to send email. Please try again.");
    }
    setSubmitting(false);
  }

  async function handleSendText(e) {
    e.preventDefault();
    if (!phone.trim() || !isValidPhone(phone)) {
      setShareError("Please enter a valid 10-digit phone number.");
      return;
    }
    setSubmitting(true);
    setShareError(false);
    try {
      let agencyData = {};
      try {
        agencyData = await searchService.getAgency(result.agency_number, { siteNumber: result.site_number, programSiteId: result.program_site_id });
      } catch { /* optional */ }
      await searchService.shareResult({
        phone: phone.replace(/\D/g, ""),
        programSiteId,
        result,
        agency: agencyData,
      });
      setModalOpen(false);
      setPhone("");
      setConfirmation(true);
      setTimeout(() => setConfirmation(false), 6000);
    } catch {
      setShareError("Unable to send text. Please try again.");
    }
    setSubmitting(false);
  }

  return (
    <>
      <span
        className="share small-label pointer"
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        aria-label={compact ? "Print and Share" : undefined}
        title={compact ? "Print and Share" : undefined}
      >
        {compact ? (
          <>
            <Share2 aria-hidden="true" size={17} strokeWidth={1.8} />
            <span className="visually-hidden">Print &amp; Share</span>
          </>
        ) : (
          <>
            <i className="ico ico-share" />
            Print &amp; Share
          </>
        )}
      </span>

      {modalOpen && (
        <div
          className="share-email modal share-modal"
          id={modalId}
          role="dialog"
          tabIndex="-1"
          style={{ display: "block", zIndex: 9999 }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <div className="actions">
                  {linkCopied ? (
                    <button className="btn copy-link-confirmation">
                      <i className="glyphicon glyphicon-check" /> Link copied
                    </button>
                  ) : (
                    <button className="btn copy-link-button" onClick={handleCopyLink}>
                      <i className="glyphicon glyphicon-copy" /> Copy link
                    </button>
                  )}
                  <button className="btn print" onClick={handlePrint}>
                    <i className="glyphicon glyphicon-print" /> Print
                  </button>
                </div>
                <h4 className="sub-headliner">Share</h4>

                {shareError && (
                  <div className="share-error" style={{ display: "block" }}>
                    {shareError}
                  </div>
                )}
              </div>

              <ul className="nav nav-tabs" role="tablist">
                <li className={activeTab === "email" ? "active" : ""} role="presentation">
                  <a
                    role="tab"
                    href={`#share-email-${programSiteId}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab("email"); }}
                  >
                    E-mail
                  </a>
                </li>
                {enableSMS && (
                  <li className={activeTab === "text" ? "active" : ""} role="presentation">
                    <a
                      role="tab"
                      href={`#share-text-${programSiteId}`}
                      onClick={(e) => { e.preventDefault(); setActiveTab("text"); }}
                    >
                      Text message
                    </a>
                  </li>
                )}
              </ul>

              <div className="tab-content">
                {activeTab === "email" && (
                  <div className="tab-pane active" role="tabpanel" id={`share-email-${programSiteId}`}>
                    <div className="modal-body share-content">
                      <label className="label-no-transform">* E-mail this information to:</label>
                      <input
                        className="input share-address"
                        name="email"
                        placeholder="List of comma-separated e-mail addresses"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />

                      <label className="label-no-transform optional">Optional information:</label>
                      <input
                        className="input share-client"
                        name="client"
                        placeholder="Name"
                        value={client}
                        onChange={(e) => setClient(e.target.value)}
                      />

                      <textarea
                        className="input share-comments"
                        name="comments"
                        placeholder="Comments and contact information"
                        rows={5}
                        wrap="hard"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                      />

                      {isSignedIn && (
                        <p>
                          <input
                            type="checkbox"
                            className="input"
                            name="reply_to_me"
                            id={`reply-to-me-${programSiteId}`}
                            checked={replyToMe}
                            onChange={(e) => setReplyToMe(e.target.checked)}
                          />
                          <label htmlFor={`reply-to-me-${programSiteId}`}> Send replies to me</label>
                        </p>
                      )}

                      <button
                        className="email-result btn btn-primary"
                        name="send"
                        onClick={handleSendEmail}
                        disabled={submitting}
                      >
                        {submitting ? "Sending..." : "Send e-mail"}
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === "text" && enableSMS && (
                  <div className="tab-pane active" role="tabpanel" id={`share-text-${programSiteId}`}>
                    <div className="modal-body share-content">
                      <label className="label-no-transform">* Text this information to:</label>
                      <input
                        className="input share-phone"
                        name="phone"
                        placeholder="Cell phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />

                      <button
                        className="email-result btn btn-primary"
                        name="send"
                        onClick={handleSendText}
                        disabled={submitting}
                      >
                        {submitting ? "Sending..." : "Send text message"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-default" type="button" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {modalOpen && <div className="modal-backdrop fade in" />}

      <div className={`share-confirmation${confirmation ? " open" : ""}`}>
        {confirmation && "You should receive your resource shortly. Thank you for using 211."}
      </div>
    </>
  );
}

export default ShareResult;
